/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { forwardRef } from 'react';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';

import { type Organisation } from '../../types';
import { SWITCHER_TAB_BUTTON_PREFIX, SWITCHER_TAB_PANEL_PREFIX } from './utils';
import { useIsSwitcherTeam25TestCohort } from '../components/experiment-provider';
import { useThemeObserver } from '@atlaskit/tokens';

const getArbitraryElement = (arr: string[], seed: string) => {
	const charCodes = seed.split('').reduce((a, b, i) => {
		return a + b.charCodeAt(0);
	}, 0);

	return arr[charCodes % arr.length];
};

interface Props extends Organisation {
	isActive: boolean;
	onClick: () => void;
	onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
	tabIndex?: number;
	percentageOpen?: number;
}

const OrganisationItem = forwardRef<HTMLButtonElement, Props>(
	({ onKeyDown, displayName, isActive, onClick, tabIndex, id, percentageOpen = 1 }, ref) => {
		const isVisualRefreshEnabled = useIsSwitcherTeam25TestCohort();
		const displayInitials = displayName.substring(0, 2);
		const { colorMode: observedColorMode } = useThemeObserver();
		const isDarkMode = observedColorMode === 'dark';

		const variantArray = isVisualRefreshEnabled ? variantsRefreshed : variants;
		const variantKey = getArbitraryElement(variantArray, displayName);

		return (
			<div css={[orgItemStyles.container, isActive && orgItemStyles.activeContainer]}>
				<Tooltip content={displayName} position="right" ignoreTooltipPointerEvents>
					{({ ref: tooltipRef, 'aria-describedby': _, ...tooltipProps }) => (
						<button
							{...tooltipProps}
							role="tab"
							aria-selected={isActive ? 'true' : 'false'}
							ref={mergeRefs([ref, tooltipRef])}
							onKeyDown={onKeyDown}
							aria-label={displayName}
							id={`${SWITCHER_TAB_BUTTON_PREFIX}-${id}`}
							aria-controls={`${SWITCHER_TAB_PANEL_PREFIX}-${id}`}
							tabIndex={tabIndex}
							onClick={onClick}
							css={[
								isVisualRefreshEnabled && orgItemStyles.tooltipContainerRefreshed,
								!isVisualRefreshEnabled && orgItemStyles.tooltipContainerOriginal,
							]}
						>
							<div
								css={[
									isVisualRefreshEnabled && orgItemStyles.tokenRefreshed,
									!isVisualRefreshEnabled && orgItemStyles.tokenOriginal,
									isVisualRefreshEnabled &&
										isDarkMode &&
										darkTokens[variantKey as keyof typeof darkTokens],
									!(isVisualRefreshEnabled && isDarkMode) &&
										lightTokens[variantKey as keyof typeof lightTokens],
									!isVisualRefreshEnabled &&
										originalTokens[variantKey as keyof typeof originalTokens],
									isActive && orgItemStyles.active,
								]}
							>
								<span aria-hidden="true" css={orgItemStyles.initial}>
									{displayInitials}
								</span>
							</div>
							{percentageOpen > 0 && (
								<span
									aria-hidden="true"
									css={orgItemStyles.text}
									style={{
										opacity: percentageOpen,
									}}
									data-testid={`switcher-org-name`}
								>
									{displayName}
								</span>
							)}
						</button>
					)}
				</Tooltip>
			</div>
		);
	},
);

const variants = [
	'red',
	'blue',
	'green',
	'yellow',
	'purple',
	'magenta',
	'teal',
	'orange',
	'lime',
	'gray',
];
const variantsRefreshed = ['purple', 'blue', 'lime', 'yellow'];

const originalTokens = cssMap({
	red: {
		backgroundColor: token('color.background.accent.red.subtlest'),
		color: token('color.text.accent.red'),
	},
	blue: {
		backgroundColor: token('color.background.accent.blue.subtler'),
		color: token('color.text.accent.blue'),
	},
	green: {
		backgroundColor: token('color.background.accent.green.subtler'),
		color: token('color.text.accent.green'),
	},
	yellow: {
		backgroundColor: token('color.background.accent.yellow.subtler'),
		color: token('color.text.accent.yellow'),
	},
	purple: {
		backgroundColor: token('color.background.accent.purple.subtler'),
		color: token('color.text.accent.purple'),
	},
	magenta: {
		backgroundColor: token('color.background.accent.magenta.subtler'),
		color: token('color.text.accent.magenta'),
	},
	teal: {
		backgroundColor: token('color.background.accent.teal.subtler'),
		color: token('color.text.accent.teal'),
	},
	orange: {
		backgroundColor: token('color.background.accent.orange.subtler'),
		color: token('color.text.accent.orange'),
	},
	lime: {
		backgroundColor: token('color.background.accent.lime.subtler'),
		color: token('color.text.accent.lime'),
	},
	gray: {
		backgroundColor: token('color.background.accent.gray.subtler'),
		color: token('color.text.accent.gray'),
	},
});

const lightTokens = cssMap({
	purple: {
		backgroundColor: token('color.background.accent.purple.subtlest'),
		color: token('color.text.accent.purple.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		},
	},
	blue: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		color: token('color.text.accent.blue.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtlest.hovered'),
		},
	},
	lime: {
		backgroundColor: token('color.background.accent.lime.subtlest'),
		color: token('color.text.accent.lime.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.lime.subtlest.hovered'),
		},
	},
	yellow: {
		backgroundColor: token('color.background.accent.yellow.subtlest'),
		color: token('color.text.accent.yellow.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.yellow.subtlest.hovered'),
		},
	},
});

const darkTokens = cssMap({
	purple: {
		backgroundColor: token('color.background.accent.purple.subtler'),
		color: token('color.text.accent.purple.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.purple.subtler.hovered'),
		},
	},
	blue: {
		backgroundColor: token('color.background.accent.blue.subtler'),
		color: token('color.text.accent.blue.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.blue.subtler.hovered'),
		},
	},
	lime: {
		backgroundColor: token('color.background.accent.lime.subtler'),
		color: token('color.text.accent.lime.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.lime.subtler.hovered'),
		},
	},
	yellow: {
		backgroundColor: token('color.background.accent.yellow.subtler'),
		color: token('color.text.accent.yellow.bolder'),
		'&:hover': {
			backgroundColor: token('color.background.accent.yellow.subtler.hovered'),
		},
	},
});

const orgItemStyles = cssMap({
	initial: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '28px',
	},
	text: {
		font: token('font.body'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		fontWeight: token('font.weight.medium'),
		margin: '0',
		color: token('color.text'),
		display: '-webkit-box',
		overflow: 'hidden',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
		wordBreak: 'break-all',
		height: '100%',
		paddingLeft: token('space.100'),
		textOverflow: 'ellipsis',
		textAlign: 'left',
		transition: 'opacity 300ms',
	},
	container: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		position: 'relative',
		'&::before': {
			content: '""',
			position: 'absolute',
			left: '0px',
			top: '0px',
			bottom: '0px',
			width: '3px',
			borderTopRightRadius: token('border.radius.050'),
			borderBottomRightRadius: token('border.radius.050'),
		},
		'&:hover': {
			borderColor: token('color.background.brand.bold.hovered'),
			'&::before': {
				backgroundColor: token('color.background.brand.bold.hovered'),
			},
		},
		'&:active': {
			borderColor: token('color.background.brand.bold.pressed'),
			'&::before': {
				backgroundColor: token('color.background.brand.bold.pressed'),
			},
		},
	},
	activeContainer: {
		borderColor: token('color.background.brand.bold'),
		'&::before': {
			backgroundColor: token('color.background.brand.bold'),
		},
	},
	tooltipContainerOriginal: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		background: 'transparent',
		cursor: 'pointer',
		borderColor: 'transparent',
		borderWidth: '0px',
		borderStyle: 'solid',
		width: '100%',
		paddingInline: '0',
		paddingBlock: '0',
		'&:hover': {
			opacity: 0.8,
		},
	},
	tooltipContainerRefreshed: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		background: 'transparent',
		cursor: 'pointer',
		borderColor: 'transparent',
		borderWidth: '0px',
		borderStyle: 'solid',
		width: '100%',
		paddingInline: token('space.025'),
		paddingBlock: token('space.025'),
	},
	tokenOriginal: {
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		font: token('font.body.large'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		background: 'transparent',
		paddingInline: '0',
		paddingBlock: '0',
		boxSizing: 'content-box',
		textAlign: 'center',
		verticalAlign: 'middle',
		textTransform: 'capitalize',
		color: token('color.text.inverse'),
		borderRadius: token('border.radius.200'),
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: '2px',
		width: '28px',
		height: '28px',
		flex: 'none',
	},
	tokenRefreshed: {
		font: token('font.body'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		fontWeight: token('font.weight.medium'),
		background: 'transparent',
		paddingInline: '0',
		paddingBlock: '0',
		boxSizing: 'content-box',
		textAlign: 'center',
		verticalAlign: 'middle',
		textTransform: 'capitalize',
		color: token('color.text.inverse'),
		borderRadius: token('border.radius.200'),
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: '2px',
		width: '28px',
		height: '28px',
		flex: 'none',
	},
	active: {
		borderColor: token('color.background.brand.bold'),
		'&:focus-visible': {
			borderColor: token('color.background.brand.bold.hovered'),
			opacity: 0.8,
			outline: 0,
		},
	},
});

export default OrganisationItem;
